import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAdminArmario, type Prenda } from '../../../hooks/useAdminArmario';
import { useAdminClientesAsesoria } from '../../../hooks/useAdminClientesAsesoria';
import {
  categoriasPrenda,
  ocasionesPrenda,
  estacionesPrenda,
} from '../../../mocks/clienteArmario';

const PAGE_SIZE = 8;

interface Props {
  selectedClienteId?: number;
}

export default function AdminArmario({ selectedClienteId }: Props) {
  const { data: clientesData } = useAdminClientesAsesoria();
  const { data, add, update, remove, refresh, uploadImage, loading } = useAdminArmario([], selectedClienteId);

  const [selectedCliente, setSelectedCliente] = useState<number>(selectedClienteId || 0);
  const [clientes, setClientes] = useState(clientesData);

  useEffect(() => {
    setClientes(clientesData);
    if (selectedClienteId && selectedClienteId > 0) {
      setSelectedCliente(selectedClienteId);
    } else if (clientesData.length > 0 && selectedCliente === 0) {
      setSelectedCliente(clientesData[0].id);
    }
  }, [clientesData, selectedClienteId, selectedCliente]);

  const [editing, setEditing] = useState<Prenda | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('todas');
  const [formError, setFormError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const prendasCliente = data.filter((p) => p.cliente_id === selectedCliente);

  const filtered = prendasCliente.filter((p) => {
    const matchSearch =
      p.nombre.toLowerCase().includes(search.toLowerCase()) ||
      p.color.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoriaFilter === 'todas' || p.categoria === categoriaFilter;
    return matchSearch && matchCat;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const paged = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  const currentCliente = clientes.find((c) => c.id === selectedCliente);

  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const doUpload = useCallback(async (file: File) => {
    if (!selectedCliente) return;
    setUploadingImage(true);
    const url = await uploadImage(file, selectedCliente);
    if (url) setPreviewUrl(url);
    setUploadingImage(false);
  }, [selectedCliente, uploadImage]);

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await doUpload(file);
    e.currentTarget.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    await doUpload(file);
  };

  const handleSave = async (formData: FormData) => {
    setFormError(null);
    const cliente_id = selectedCliente;
    const nombre = (formData.get('nombre') as string)?.trim();
    const categoria = (formData.get('categoria') as string);
    const color = (formData.get('color') as string)?.trim();
    const ocasionRaw = (formData.get('ocasion') as string) || '';
    const ocasion = ocasionRaw ? ocasionRaw.split(',').map((s) => s.trim()).filter(Boolean) : [];
    const imagen_url = previewUrl ?? (editing?.imagen_url || null);
    const talla = (formData.get('talla') as string)?.trim() || null;
    const material = (formData.get('material') as string)?.trim() || null;
    const estacionRaw = (formData.get('estacion') as string) || '';
    const estacion = estacionRaw ? estacionRaw.split(',').map((s) => s.trim()).filter(Boolean) : [];
    const notas = (formData.get('notas') as string)?.trim() || null;

    if (!nombre || !categoria || !color) {
      setFormError('Nombre, categoría y color son obligatorios.');
      return;
    }

    const payload: Omit<Prenda, 'id'> = {
      cliente_id,
      nombre,
      categoria,
      color,
      ocasion,
      imagen_url,
      frecuencia_uso: editing?.frecuencia_uso ?? 0,
      estacion,
      talla,
      material,
      notas,
      activa: true,
      sort_order: editing?.sort_order ?? data.length,
    };

    if (editing) {
      await update(editing.id, payload);
      setEditing(null);
      setPreviewUrl(null);
    } else {
      await add(payload);
      setIsAdding(false);
      setPreviewUrl(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header con selector de cliente */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <h2 className="font-serif text-brand-black text-2xl font-medium">
            Armario
          </h2>
          <div className="relative">
            <select
              value={selectedCliente}
              onChange={(e) => {
                setSelectedCliente(Number(e.target.value));
                setPage(1);
              }}
              className="appearance-none bg-white border border-brand-gray-200 rounded-lg pl-4 pr-10 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black cursor-pointer"
            >
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-brand-gray-400">
              <i className="ri-arrow-down-s-line" />
            </div>
          </div>
          {currentCliente && (
            <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-sage/20 text-brand-black">
              {currentCliente.plan_contratado || 'Sin plan'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <select
            value={categoriaFilter}
            onChange={(e) => { setCategoriaFilter(e.target.value); setPage(1); }}
            className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          >
            <option value="todas">Todas las categorías</option>
            {categoriasPrenda.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Buscar prenda..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full sm:w-56 bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          />
          <button
            onClick={() => { setIsAdding(true); setEditing(null); setFormError(null); }}
            className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
          >
            + Nueva prenda
          </button>
          <button
            onClick={refresh}
            className="w-10 h-10 rounded-lg bg-white border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:text-brand-black hover:border-brand-black transition-colors"
            title="Recargar"
          >
            <i className="ri-refresh-line" />
          </button>
        </div>
      </div>

      {currentCliente && (
        <div className="bg-brand-sage/10 border border-brand-sage/30 rounded-xl px-5 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-brand-sage/30 flex items-center justify-center text-brand-black">
            <i className="ri-user-line" />
          </div>
          <div>
            <p className="font-sans text-brand-black text-sm font-medium">{currentCliente.nombre}</p>
            <p className="font-sans text-brand-gray-600 text-xs">
              {prendasCliente.length} prendas registradas · {currentCliente.email}
            </p>
          </div>
        </div>
      )}

      {/* Formulario */}
      {(isAdding || editing) && (
        <form
          onSubmit={(e) => { e.preventDefault(); handleSave(new FormData(e.currentTarget)); }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-5"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Prenda' : 'Nueva Prenda'}
            </h3>
            <button
              type="button"
              onClick={() => { setEditing(null); setIsAdding(false); setFormError(null); setIsDragging(false); }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 mt-0.5" />
              <p className="font-sans text-red-600 text-sm">{formError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Nombre *</label>
              <input name="nombre" defaultValue={editing?.nombre ?? ''} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Categoría *</label>
              <select name="categoria" defaultValue={editing?.categoria ?? ''} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black bg-white">
                <option value="">Seleccionar</option>
                {categoriasPrenda.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Color *</label>
              <input name="color" defaultValue={editing?.color ?? ''} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Talla</label>
              <input name="talla" defaultValue={editing?.talla ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Material</label>
              <input name="material" defaultValue={editing?.material ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Ocasiones (separadas por coma)</label>
              <input name="ocasion" defaultValue={editing?.ocasion?.join(', ') ?? ''} placeholder="trabajo, social, eventos..." className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Estaciones (separadas por coma)</label>
              <input name="estacion" defaultValue={editing?.estacion?.join(', ') ?? ''} placeholder="primavera, verano, otoño..." className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">URL de imagen</label>
              <input name="imagen_url" defaultValue={editing?.imagen_url ?? ''} placeholder="https://..." className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
          </div>

          {/* Upload directo de imagen con drag & drop */}
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">O subir foto directamente</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`mt-2 rounded-xl border-2 border-dashed p-6 flex flex-col items-center justify-center gap-3 transition-colors ${
                isDragging
                  ? 'border-brand-black bg-brand-sage/10'
                  : 'border-brand-gray-200 bg-brand-gray-50 hover:border-brand-gray-400'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                isDragging ? 'bg-brand-sage/20 text-brand-black' : 'bg-brand-gray-100 text-brand-gray-400'
              }`}>
                <i className="ri-upload-cloud-2-line text-2xl" />
              </div>
              <div className="text-center">
                <p className="font-sans text-brand-black text-sm font-medium">
                  {uploadingImage ? 'Subiendo...' : isDragging ? 'Suelta la foto aquí' : 'Arrastra y suelta una foto o haz clic'}
                </p>
                <p className="font-sans text-brand-gray-400 text-xs mt-1">JPG, PNG, WEBP · máx. 5 MB</p>
              </div>
              <label className="inline-flex items-center gap-2 bg-white border border-brand-gray-200 hover:border-brand-black transition-colors rounded-lg px-4 py-2 font-sans text-sm text-brand-gray-700 cursor-pointer mt-1">
                <i className="ri-image-line" />
                Seleccionar archivo
                <input type="file" accept="image/*" className="hidden" onChange={handleFileInputChange} />
              </label>
              {previewUrl && (
                <span className="font-sans text-brand-sage text-xs">Imagen lista para guardar</span>
              )}
              {editing?.imagen_url && !previewUrl && (
                <span className="font-sans text-brand-gray-400 text-xs">Manteniendo imagen actual</span>
              )}
            </div>
          </div>

          {/* Preview de imagen */}
          {(previewUrl || editing?.imagen_url) && (
            <div className="border border-brand-gray-200 rounded-xl p-3 w-fit">
              <p className="font-sans text-brand-gray-500 text-xs mb-2">Vista previa</p>
              <img
                src={previewUrl || editing?.imagen_url || ''}
                alt={editing?.nombre || 'Prenda'}
                className="w-32 h-40 object-cover rounded-lg"
              />
            </div>
          )}

          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Notas</label>
            <textarea name="notas" defaultValue={editing?.notas ?? ''} rows={3} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none" />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={loading || uploadingImage} className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap disabled:opacity-60">
              {loading || uploadingImage ? 'Guardando...' : 'Guardar'}
            </button>
            <button type="button" onClick={() => { setEditing(null); setIsAdding(false); setFormError(null); setPreviewUrl(null); setIsDragging(false); }} className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap">
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Grid de prendas */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-brand-gray-50 flex items-center justify-center text-brand-gray-400 mb-4">
            <i className="ri-t-shirt-line text-2xl" />
          </div>
          <p className="font-sans text-brand-gray-500 text-sm">
            {prendasCliente.length === 0
              ? 'Este cliente aún no tiene prendas registradas. Añade la primera.'
              : 'No se encontraron prendas con esos filtros.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {paged.map((prenda) => (
            <div
              key={prenda.id}
              className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden hover:border-brand-gray-300 transition-colors group"
            >
              <div className="relative w-full h-52 bg-brand-gray-50">
                {prenda.imagen_url ? (
                  <img
                    src={prenda.imagen_url}
                    alt={prenda.nombre}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-brand-gray-300">
                    <i className="ri-t-shirt-line text-4xl" />
                  </div>
                )}
                <div className="absolute top-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => { setEditing(prenda); setIsAdding(false); setFormError(null); }}
                    className="w-8 h-8 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center text-brand-gray-600 hover:text-brand-black shadow-sm"
                    title="Editar"
                  >
                    <i className="ri-pencil-line" />
                  </button>
                  <button
                    onClick={async () => {
                      if (!confirm(`¿Eliminar "${prenda.nombre}"?`)) return;
                      await remove(prenda.id);
                    }}
                    className="w-8 h-8 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center text-brand-gray-600 hover:text-red-600 shadow-sm"
                    title="Eliminar"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
                <span className="absolute top-3 left-3 inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-white/90 backdrop-blur-sm text-brand-black">
                  {prenda.categoria}
                </span>
              </div>
              <div className="p-4 space-y-2">
                <h4 className="font-sans text-brand-black text-sm font-medium line-clamp-1">{prenda.nombre}</h4>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-sans text-brand-gray-500 text-xs">{prenda.color}</span>
                  {prenda.talla && <span className="font-sans text-brand-gray-400 text-xs">· Talla {prenda.talla}</span>}
                  {prenda.material && <span className="font-sans text-brand-gray-400 text-xs">· {prenda.material}</span>}
                </div>
                {prenda.ocasion.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {prenda.ocasion.map((o) => (
                      <span key={o} className="inline-block px-2 py-0.5 rounded-full font-sans text-[10px] uppercase tracking-wider bg-brand-gray-100 text-brand-gray-500">
                        {o}
                      </span>
                    ))}
                  </div>
                )}
                {prenda.notas && (
                  <p className="font-sans text-brand-gray-400 text-xs line-clamp-2">{prenda.notas}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border border-brand-gray-200 rounded-2xl px-5 py-3">
          <div className="font-sans text-brand-gray-500 text-sm">
            Mostrando {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filtered.length)} de {filtered.length}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40">
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} className={`w-8 h-8 rounded-lg font-sans text-sm flex items-center justify-center transition-colors ${p === safePage ? 'bg-brand-black text-white' : 'border border-brand-gray-200 text-brand-gray-600 hover:border-brand-black hover:text-brand-black'}`}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40">
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
